import React from "react";
import { Stack, Typography, Divider, Box, alpha } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import BusinessIcon from "@mui/icons-material/Business";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";
import { useAppDispatch } from "../redux/store";
import { setupCompany } from "../redux/actions/companyActions";
import { cleanFormData } from "../helpers/formHelpers";
import { companyActions } from "../redux/slices/company";

const addressSchema = Yup.object().shape({
  streetLine1: Yup.string().required("Required"),
  city: Yup.string().required("Required"),
  stateOrProvince: Yup.string().required("Required"),
  postalCode: Yup.string().required("Required"),
  country: Yup.string().required("Required"),
});

const setupSchema = Yup.object().shape({
  companyDetails: Yup.object().shape({
    name: Yup.string().required("Required"),
    contactPhone: Yup.string().required("Required"),
    contactEmail: Yup.string().email("Invalid").required("Required"),
    taxId: Yup.string().required("Required"),
    headOfficeAddress: addressSchema,
    mainBranch: Yup.object().shape({
      name: Yup.string().required("Required"),
      branchCode: Yup.string().required("Required"),
      licenseKey: Yup.string().required("Required"),
      contactEmail: Yup.string().email("Invalid").required("Required"),
      contactPhone: Yup.string().required("Required"),
      address: addressSchema,
    }),
  }),
  adminFirstName: Yup.string().required("Required"),
  adminLastName: Yup.string().required("Required"),
  adminEmail: Yup.string().email("Invalid").required("Required"),
  adminUsername: Yup.string().required("Required"),
  adminPassword: Yup.string().min(6, "Min 6 chars").required("Required"),
});

const SetupCompanyForm: React.FC<{
  loading: boolean;
  error: string | null;
}> = ({ loading = false, error = null }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const initialValues = {
    companyDetails: {
      name: "",
      contactPhone: "",
      contactEmail: "",
      website: "",
      taxId: "",
      headOfficeAddress: {
        streetLine1: "",
        streetLine2: "",
        city: "",
        stateOrProvince: "",
        postalCode: "",
        country: "Kenya",
      },
      mainBranch: {
        name: "Nairobi Headquarters",
        branchCode: "HQ-NRB",
        licenseKey: "",
        contactEmail: "",
        contactPhone: "",
        address: {
          streetLine1: "",
          streetLine2: "",
          city: "",
          stateOrProvince: "",
          postalCode: "",
          country: "Kenya",
        },
      },
    },
    adminFirstName: "",
    adminLastName: "",
    adminEmail: "",
    adminUsername: "",
    adminPassword: "",
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() => dispatch(companyActions.setErrorSetupCompany(null))}
      />
      <Formik
        initialValues={initialValues}
        validationSchema={setupSchema}
        onSubmit={(values) => {
          const payload = cleanFormData(values);
          dispatch(setupCompany(payload, navigate));
        }}
      >
        {({ isValid }: FormikProps<typeof initialValues>) => (
          <Form>
            <Stack spacing={4}>
              <Box>
                <Typography
                  variant="h6"
                  sx={{ color: "primary.main", fontWeight: 700, mb: 2 }}
                >
                  1. Company Identity
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" },
                  }}
                >
                  <FormTextField
                    name="companyDetails.name"
                    label="Company Registered Name"
                  />
                  <FormTextField
                    name="companyDetails.taxId"
                    label="Tax ID / PIN"
                  />
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    mt: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                  }}
                >
                  <FormTextField
                    name="companyDetails.contactEmail"
                    label="Email"
                  />
                  <FormTextField
                    name="companyDetails.contactPhone"
                    label="Phone"
                  />
                  <FormTextField
                    name="companyDetails.website"
                    label="Website (Optional)"
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: (theme) =>
                    alpha(theme.palette.background.default, 0.5),
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700 }}>
                  Head Office Address
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  }}
                >
                  <FormTextField
                    name="companyDetails.headOfficeAddress.streetLine1"
                    label="Street"
                  />
                  <FormTextField
                    name="companyDetails.headOfficeAddress.city"
                    label="City"
                  />
                  <FormTextField
                    name="companyDetails.headOfficeAddress.stateOrProvince"
                    label="State"
                  />
                  <FormTextField
                    name="companyDetails.headOfficeAddress.postalCode"
                    label="Postal Code"
                  />
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography
                  variant="h6"
                  sx={{ color: "secondary.main", fontWeight: 700, mb: 2 }}
                >
                  2. Main Branch & License
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr" },
                  }}
                >
                  <FormTextField
                    name="companyDetails.mainBranch.name"
                    label="Branch Name"
                  />
                  <FormTextField
                    name="companyDetails.mainBranch.branchCode"
                    label="Code"
                  />
                  <FormTextField
                    name="companyDetails.mainBranch.licenseKey"
                    label="ERP License Key"
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: (theme) =>
                    alpha(theme.palette.background.default, 0.5),
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700 }}>
                  Branch Administrator Account
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  }}
                >
                  <FormTextField name="adminFirstName" label="First Name" />
                  <FormTextField name="adminLastName" label="Last Name" />
                  <FormTextField name="adminUsername" label="Username" />
                  <FormTextField
                    name="adminPassword"
                    label="Password"
                    type="password"
                  />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <FormTextField
                    name="adminEmail"
                    label="Admin Recovery Email"
                  />
                </Box>
              </Box>

              <SubmitButton
                title="Initialize ERP System"
                loading={loading}
                disabled={!isValid}
                startIcon={<BusinessIcon />}
                fullWidth
                size="large"
                sx={{ py: 2, fontWeight: 800 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
export default SetupCompanyForm;
